import {
	Column,
	Entity,
	Index,
	JoinColumn,
	ManyToOne,
	OneToMany,
	OneToOne,
} from 'typeorm';
import { BaseEntity } from './base.entity';
import { ConversationAgentEntity } from './conversation-agent.entity';
import { ConversationCallEntity } from './conversation-call.entity';
import { ConversationMessageEntity } from './conversation-message.entity';

@Entity('_conversation_session')
@Index(['ownerUserId', 'status'])
@Index('idx_conversation_session_created_date', ['createdDate'])
@Index('idx_conversation_session_agent', ['startingAgentId', 'createdDate'])
@Index('idx_conversation_session_call', ['callId'])
export class ConversationSessionEntity extends BaseEntity {
	@Column({ name: 'user_id', type: 'int', unsigned: true, nullable: false })
	public userId!: number;

	@Column({
		name: 'session_uuid',
		type: 'varchar',
		length: 36,
		nullable: false,
		unique: true,
		comment: 'Unique identifier for the session used for queue operations',
	})
	@Index()
	public sessionUuid!: string;

	@Column({
		name: 'fk__conversation_agent__id_starting',
		type: 'int',
		unsigned: true,
		nullable: true,
		comment: 'The agent ID used to start this conversation session',
	})
	public startingAgentId?: number;

	@ManyToOne(() => ConversationAgentEntity, { nullable: true })
	@JoinColumn({
		name: 'fk__conversation_agent__id_starting',
		referencedColumnName: 'id',
	})
	public startingAgent?: ConversationAgentEntity;

	@Column({
		name: 'context',
		type: 'json',
		nullable: true,
		comment: 'Stored conversation context and metadata',
	})
	public context?: any;

	@Column({
		name: 'status',
		type: 'varchar',
		length: 50,
		default: "'active'",
	})
	public status!: 'active' | 'archived' | 'error';

	@Column({
		name: 'last_activity',
		type: 'timestamp',
		default: () => 'CURRENT_TIMESTAMP',
	})
	public lastActivity!: Date;

	@Column({
		name: 'token_count',
		type: 'int',
		default: 0,
		comment: 'Total tokens used in this session',
	})
	public tokenCount!: number;

	@Column({
		name: 'message_count',
		type: 'int',
		default: 0,
		comment: 'Total message count in this session',
	})
	public messageCount!: number;

	@Column({
		name: 'metadata',
		type: 'json',
		nullable: true,
		comment: 'Additional session metadata',
	})
	public metadata?: any;

	/**
	 * Serialized `MemoryStore` state (variables + known entity ids).
	 * Persisted by `ConversationManager` at every turn-end so a
	 * cold-start restore (load-from-DB on inbound SMS, or voice
	 * reconnection beyond the 60s Redis TTL) can re-hydrate memory
	 * state via `MemoryStore.restore(serialized)`. Prior to this
	 * column, memory state was Redis-only and lost after the 60s
	 * reconnection grace expired.
	 *
	 * Shape: `SerializedMemoryState` from `apps/crm-api/src/modules/
	 * ai-agents/memory-store.ts` —
	 *   { knownEntityIds: Record<EntityType, number[]>,
	 *     variables: Record<string, { value, immutable }> }
	 *
	 * Null on legacy sessions that pre-date this column. Readers
	 * should treat null as "use a fresh empty MemoryStore."
	 */
	@Column({
		name: 'memory_state',
		type: 'json',
		nullable: true,
		comment:
			'Serialized SerializedMemoryState — restored on cold-start via MemoryStore.restore()',
	})
	public memoryState?: any;

	@Column({
		name: 'conversation_mode',
		type: 'enum',
		enum: ['voice', 'chat'],
		nullable: true,
		comment: 'Conversation mode - voice or chat, set at session start',
	})
	public conversationMode?: 'voice' | 'chat';

	@Column({
		name: 'stt_provider',
		type: 'varchar',
		length: 50,
		nullable: true,
		comment: 'STT provider used (deepgram, etc.)',
	})
	public sttProvider?: string;

	@Column({
		name: 'stt_model',
		type: 'varchar',
		length: 100,
		nullable: true,
		comment: 'Specific STT model used',
	})
	public sttModel?: string;

	@Column({
		name: 'started_at',
		type: 'timestamp',
		nullable: true,
		comment: 'When the session actually started (first interaction)',
	})
	public startedAt?: Date;

	@Column({
		name: 'ended_at',
		type: 'timestamp',
		nullable: true,
		comment: 'When the session ended',
	})
	public endedAt?: Date;

	@Column({
		name: 'summary',
		type: 'text',
		nullable: true,
		comment: 'Summary of the conversation',
		charset: 'utf8mb4',
		collation: 'utf8mb4_0900_ai_ci',
	})
	public summary?: string;

	@Column({
		name: 'summary_llm_provider',
		type: 'varchar',
		length: 50,
		nullable: true,
		comment: 'LLM provider used for summary generation',
	})
	public summaryLlmProvider?: string;

	@Column({
		name: 'summary_llm_model',
		type: 'varchar',
		length: 100,
		nullable: true,
		comment: 'LLM model used for summary generation',
	})
	public summaryLlmModel?: string;

	@Column({
		name: 'summary_llm_execution_time_ms',
		type: 'int',
		nullable: true,
		comment: 'Time taken for summary generation in milliseconds',
	})
	public summaryLlmExecutionTimeMs?: number;

	@Column({
		name: 'summary_llm_tokens',
		type: 'int',
		nullable: true,
		comment: 'Number of tokens used for summary generation',
	})
	public summaryLlmTokens?: number;

	@Column({
		name: 'fk__conversation_call__id',
		type: 'int',
		unsigned: true,
		nullable: true,
		comment: 'Reference to the call that initiated this session',
	})
	public callId?: number;

	@OneToOne(() => ConversationCallEntity, (call) => call.session, {
		nullable: true,
	})
	@JoinColumn({
		name: 'fk__conversation_call__id',
		referencedColumnName: 'id',
	})
	public call?: ConversationCallEntity;

	@OneToMany(() => ConversationMessageEntity, (message) => message.session, {
		cascade: false,
	})
	public messages!: ConversationMessageEntity[];
}
